import Tooltip from '@mui/material/Tooltip';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import checkSvg from '../../../assets/img/check-circle.svg';
import { getUserType } from '../../../redux/slices/userSlice';
import { ICL, SelectOneTask, TaskKeysType, TestItem, WorkType } from '../../../shared/types';
import { ExcludeAnswer, SelectOneAnswer } from '../../../shared/types/task';
import StudentTaskWrapper from '../StudentTaskWrapper';

interface StudentExcludeTaskProps extends SelectOneTask {
  icl: ICL | null;
  handleChangeTaskByKey: (
    id: string,
    key: TaskKeysType,
    value: string[] | string | boolean | ExcludeAnswer[] | TestItem[] | SelectOneAnswer[],
  ) => void;
  saveTaskAnswer: (id: string) => Promise<void>;
  hw?: boolean;
  preview?: boolean;
  studentName?: string;
  index: number;
  points: number;
  workTaskType?: WorkType;
}

export default function StudentSelectOneTask({
  icl,
  text,
  linesAnswer,
  hasAnswer,
  hasStar,
  checkedAt,
  completedAt,
  handleChangeTaskByKey,
  id,
  hasPhoto,
  answersPhotos,
  saveTaskAnswer,
  deadline,
  audioUrl,
  videoUrl,
  taskPhotos,
  editedAnswerPhotos,
  hw,
  pdfUrl,
  wordUrl,
  studentPdfUrl,
  studentWordUrl,
  preview,
  lines,
  type,
  answer,
  studentName,
  index,
  points,
  workTaskType,
  teacherPoints,
}: StudentExcludeTaskProps) {
  const userType = useSelector(getUserType);
  const { t } = useTranslation();

  const handleSetAnswer = (lineUuid: string, wordId: number, word: 'first' | 'second') => {
    if (userType === 'Student' && !checkedAt) {
      const prevAnswer = (linesAnswer ?? []).find(it => it.lineId === lineUuid && it.wordId === wordId) ?? null;
      const answer = { lineId: lineUuid, wordId, firstCorrect: word === 'first' };
      const outputAnswerArr = (linesAnswer ?? []).filter(it => it.lineId !== lineUuid || it.wordId !== wordId);

      handleChangeTaskByKey(
        id,
        'linesAnswer',
        prevAnswer?.firstCorrect === answer.firstCorrect ? outputAnswerArr : [...outputAnswerArr, answer],
      );
    }
  };

  const renderTeacherChoices = (firstCorrect: boolean, word: 'first' | 'second') => {
    if (userType !== 'Student' && firstCorrect === (word === 'first')) {
      return (
        <Tooltip
          title={t('wordChoiseTeacher')}
          componentsProps={{
            popper: {
              sx: {
                ['& .MuiTooltip-arrow']: {
                  color: '#9CDFFC',
                },
                ['& .MuiTooltip-tooltip']: {
                  backgroundColor: '#9CDFFC',
                  color: '#000000',
                  fontSize: '14px',
                  padding: '14px',
                  borderRadius: '8px',
                },
              },
            },
          }}
          arrow
        >
          <img src={checkSvg} alt="check" />
        </Tooltip>
      );
    }
  };

  const setSelectedAnswer = (lineUuid: string, wordId: number, word: 'first' | 'second') => {
    const answer = linesAnswer?.find(a => a.lineId === lineUuid && a.wordId === wordId) ?? null;
    return answer?.firstCorrect === (word === 'first') ? 'border-[#52C7FA] border-2' : '';
  };

  const renderLines = () => {
    return lines.map(({ id: lineId, words, description, descriptionAfter }, lineIndex) => (
      <div key={lineId} className="!mb-4 mt-[12px] flex w-full items-center justify-between">
        <div className="flex items-center">
          <div className="text-[18px] font-medium text-[#242C2C]">{lineIndex + 1}.</div>

          <div className="!ml-2 text-[18px] font-medium text-[#242C2C]">{description}</div>

          <div className="!ml-2	flex flex-wrap items-center gap-y-[12px] text-[18px] font-medium text-[#242C2C]">
            {words.map((word, wordId) => {
              if (typeof word === 'object') {
                const { first, second, firstCorrect } = word;

                return (
                  <div className="mr-[8px] flex items-center gap-[12px]" key={wordId}>
                    <div
                      onClick={() => handleSetAnswer(lineId, wordId, 'first')}
                      className={`flex cursor-pointer items-center gap-[10px] ${setSelectedAnswer(lineId, wordId, 'first')} rounded-[10px] bg-[#DCF0FF] p-[10px]`}
                    >
                      {renderTeacherChoices(firstCorrect as boolean, 'first')} {first}
                    </div>

                    <div>/</div>

                    <div
                      onClick={() => handleSetAnswer(lineId, wordId, 'second')}
                      className={`flex cursor-pointer items-center gap-[10px] ${setSelectedAnswer(lineId, wordId, 'second')} rounded-[10px] bg-[#DCF0FF] p-[10px]`}
                    >
                      {renderTeacherChoices(firstCorrect as boolean, 'second')} {second}
                    </div>
                  </div>
                );
              } else {
                return (
                  <div className="mr-[8px]" key={wordId}>
                    {word}
                  </div>
                );
              }
            })}
          </div>

          <div className="!ml-2 text-[18px] font-medium text-[#242C2C]">{descriptionAfter}</div>
        </div>
      </div>
    ));
  };

  const isDisabledSubmitButton = !linesAnswer || !!checkedAt || !!completedAt;

  return (
    <StudentTaskWrapper
      icl={icl}
      taskText={text}
      answersPhotos={answersPhotos}
      id={id}
      handleChangeTaskByKey={handleChangeTaskByKey}
      checkedAt={checkedAt}
      hasPhoto={hasPhoto}
      completedAt={completedAt}
      saveTaskAnswer={saveTaskAnswer}
      deadline={deadline}
      audioUrl={audioUrl}
      hasAnswer={hasAnswer}
      hasStar={hasStar}
      videoUrl={videoUrl}
      taskPhotos={taskPhotos}
      editedAnswerPhotos={editedAnswerPhotos}
      hw={hw}
      pdfUrl={pdfUrl}
      wordUrl={wordUrl}
      studentPdfUrl={studentPdfUrl}
      studentWordUrl={studentWordUrl}
      preview={preview}
      isDisabledSubmitButton={isDisabledSubmitButton}
      isSubmitButton
      answer={answer}
      taskName={'practiceTask'}
      studentName={studentName}
      index={index}
      points={points}
      workTaskType={workTaskType}
      teacherPoints={teacherPoints}
    >
      <>{renderLines()}</>
    </StudentTaskWrapper>
  );
}
